import { POST } from '@/api'
import { format, parse, isAfter, isSameDay, addMinutes } from 'date-fns'
import { keyBy, mapValues, sortBy, sumBy, minBy } from 'lodash'
import { today, toHourClock } from '@/utils/date'
import { imminentReservations, isOverlap } from '@/utils/reserve'
import { checkStatus } from '@/resources/reservation/statusMap'
import detail from './detail'
import edit from './edit'
import cancel from './cancel'
import history from './history'
import syncMails from './syncMails'
import resraku from './resraku'
import cancelMail from './cancelMail'
import csv from './csv'
import list from './list'
import step1 from './step1'

const initialReserve = {
  id: null,
  status: null,
  receiptDatetime: null,
  reserveDatetime: null,
  utilizationTime: 0,
  resetTime: 0,
  reserveName: null,
  adults: 0,
  children: 0,
  tableIds: [],
  courseIds: [],
  tags: [],
  hasFreehandMemo: false,
  syncType: null,
  staffName: null,
}

const initialCustomer = {
  id: null,
  lastName: null,
  lastNameKana: null,
  firstName: null,
  firstNameKana: null,
  tel1: null,
  tel2: null,
  mail1: null,
  mail2: null,
  officeName: null,
  visitCount: 0,
  attributes: {},
}

/**
 * 予約
 */
export default {
  namespaced: true,
  state: {
    byId: {},
    allIds: [],
    busy: false,
  },
  modules: {
    detail,
    edit,
    cancel,
    history,
    syncMails,
    cancelMail,
    resraku,
    csv,
    list,
    step1,
  },
  mutations: {
    set(state, reserves) {
      state.byId = keyBy(
        reserves.map(reserve => ({
          ...initialReserve,
          ...reserve,
          customer: { ...initialCustomer, ...reserve.customer },
        })),
        'id',
      )
      state.allIds = reserves.map(reserve => reserve.id)
    },
    update(state, reserves) {
      state.byId = Object.assign(
        state.byId,
        keyBy(
          [].concat(reserves).map(reserve => ({
            ...initialReserve,
            ...state.byId[reserve.id],
            ...reserve,
          })),
          'id',
        ),
      )
    },
    busy(state, busy) {
      state.busy = busy
    },
    updateReservationStatus(state, { reservationId, statusId }) {
      if (statusId === 1) {
        state.allIds = state.allIds.filter(id => id !== reservationId)
      }
      state.byId[reservationId].status = statusId
    },
  },
  getters: {
    all({ byId, allIds }) {
      return allIds.map(id => byId[id])
    },
    /**
     * 来店時間, 受付時間の昇順にソートされた予約一覧を返却します
     */
    ascTimeReserves(state, { all }) {
      return sortBy(all, ['reserveDatetime', 'receiptDatetime'])
    },
    byTableId(state, { all }) {
      return mapValues(
        all.reduce((byTableId, reserve) => {
          reserve.tableIds.forEach(tableId => (byTableId[tableId] = (byTableId[tableId] || []).concat(reserve)))
          return byTableId
        }, {}),
        reserves => sortBy(reserves, ['reserveDatetime']),
      )
    },
    byTableIdByZone(state, { byTableId }, rootState, { 'stores/zone/typeByTime': zoneTypeByTime }) {
      return mapValues(byTableId, reserves =>
        reserves.reduce(
          (byZone, reserve) => {
            const zone = zoneTypeByTime(toHourClock(reserve.reserveDatetime).time)
            const zoneByEndTime = zoneTypeByTime(
              toHourClock(addMinutes(reserve.reserveDatetime, reserve.utilizationTime)).time,
            )
            byZone[zone].push(reserve)

            if (checkStatus.isBlock(reserve.status) && zone !== zoneByEndTime) {
              byZone[zoneByEndTime].push(reserve)
            }

            return byZone
          },
          {
            lunch: [],
            dinner: [],
          },
        ),
      )
    },
    overlapIds({ byId, allIds }) {
      return allIds.filter(a => allIds.some(b => isOverlap(byId[a], byId[b])))
    },
    /**
     * 予約毎のテーブルに対して超過している予約人数を返します
     */
    overCapacityById({ byId }, getters, { tables }) {
      const tablesById = (tables && tables.byId) || {}
      return mapValues(byId, ({ adults = 0, children = 0, tableIds }) =>
        Math.max(adults + children - sumBy(tableIds, tableId => (tablesById[tableId] || {}).maxAvailableSeat), 0),
      )
    },
    overlapingByTableId(state, { byTableId }) {
      return mapValues(byTableId, (reserves, id) => reserves.some(a => byTableId[id].some(b => isOverlap(a, b))))
    },

    /**
     * 隣接テーブルによる予約バーの結合状態を、予約ID毎、テーブルID毎に返します
     * @returns {object} 予約ID毎／テーブルID毎の結合状態 start, middle, end
     */
    joinTypeByIdByTableId({ byId }, getters, { tables }) {
      const allTableIds = tables.allIds
      const lastTableIndex = allTableIds.length - 1
      return mapValues(byId, ({ tableIds }) => {
        if (tableIds.length === 1) return {}
        return tableIds.reduce((style, tableId) => {
          const tableIndex = allTableIds.indexOf(tableId)
          const isStart = !tableIndex || !tableIds.includes(allTableIds[tableIndex - 1])
          const isEnd = tableIndex === lastTableIndex || !tableIds.includes(allTableIds[tableIndex + 1])
          if (!isStart || !isEnd) style[tableId] = (isStart && 'start') || (isEnd && 'end') || 'middle'
          return style
        }, {})
      })
    },
    /**
     * 現在時刻を軸にレイアウト表示内でテーブル上に表示する有効な予約を抽出し、テーブルID毎及びランチ/ディナー毎に返却します
     * 本日のレイアウト画面の場合は、予約時間を経過した予約は除外します
     * 抽出の条件は仕様書011_テーブルレイアウト画面.pptxをご確認ください
     */
    recentReservesByTableIdByZone: (state, { byTableIdByZone }, rootState) => date => {
      const isToday = isSameDay(date, today())
      const now = new Date()
      const { lunchUtilization, dinnerUtilization, reset } = rootState.stores.current.store.time
      return isToday
        ? mapValues(byTableIdByZone, zone => {
            const { lunch, dinner } = zone
            return {
              lunch: imminentReservations(lunch, now, lunchUtilization, reset),
              dinner: imminentReservations(dinner, now, dinnerUtilization, reset),
            }
          })
        : byTableIdByZone
    },
    /**
     * 現在時刻を軸に有効な最新の予約を一つ抽出し、そのステータスをテーブルID毎に返却します
     * 本日のレイアウト画面の場合は、予約時間を経過した予約は除外します
     * 抽出の条件は仕様書011_テーブルレイアウト画面.pptxをご確認ください
     */
    recentReserveStatusByTableId: (state, { byTableIdByZone }, rootState) => date => {
      const isToday = isSameDay(date, today())
      const isAfterTomorrow = isAfter(date, today())
      const now = new Date()
      const { lunchUtilization, dinnerUtilization, reset } = rootState.stores.current.store.time
      return mapValues(byTableIdByZone, zone => {
        const lunchReservations = imminentReservations(zone.lunch, now, lunchUtilization, reset)
        const dinnerReservations = imminentReservations(zone.dinner, now, dinnerUtilization, reset)

        if (isToday) {
          return {
            lunch: minBy(
              lunchReservations.length > 1
                ? lunchReservations.filter(r => !checkStatus.isBlock(r.status))
                : lunchReservations,
              'reserveDatetime',
            )?.status,
            dinner: minBy(
              dinnerReservations.length > 1
                ? dinnerReservations.filter(r => !checkStatus.isBlock(r.status))
                : dinnerReservations,
              'reserveDatetime',
            )?.status,
          }
        } else if (isAfterTomorrow) {
          return {
            lunch:
              zone.lunch.length > 1
                ? zone.lunch.find(r => isAfter(r.reserveDatetime, now) && !checkStatus.isBlock(r.status))?.status
                : zone.lunch.find(r => isAfter(r.reserveDatetime, now))?.status,
            dinner:
              zone.dinner.length > 1
                ? zone.dinner.find(r => isAfter(r.reserveDatetime, now) && !checkStatus.isBlock(r.status))?.status
                : zone.dinner.find(r => isAfter(r.reserveDatetime, now))?.status,
          }
        }

        return {
          lunch: zone.lunch.find(r => isAfter(r.reserveDatetime, now))?.status,
          dinner: zone.dinner.find(r => isAfter(r.reserveDatetime, now))?.status,
        }
      })
    },
    peoples: (state, { all }) => all.reduce((sum, { adults, children }) => sum + adults + children, 0),
  },
  actions: {
    async get(store, { date, readonly = false }) {
      const { commit, rootState } = store
      commit('busy', true)

      const currentStore = rootState.stores.current.store
      const { status, message, data } = await POST('/api/web/v2/reservation/list', {
        date: format(date, 'YYYYMMDD'),
        storeId: currentStore.id,
        readonly,
      })
      if (status === 200) {
        commit(
          'set',
          data.reservations.map(reserve => ({
            ...reserve,
            receiptDatetime: reserve.receiptDatetime ? parse(reserve.receiptDatetime) : null,
            reserveDatetime: reserve.reserveDatetime ? parse(reserve.reserveDatetime) : null,
          })),
        )
      } else {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200
    },
  },
}
